// src/pages/Settings.jsx
import React, { useEffect, useState, useContext, useRef } from "react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { AuthContext } from "../context/AuthContext";
import ConfirmModal from "../components/ConfirmModal";
import api, {
  getAccessToken as getApiAccessToken,
  refreshAccessToken,
  logoutSession,
} from "../api";
import { restorePurchases, isNative } from "@/iap";
import { Purchases } from "@revenuecat/purchases-capacitor";
import { useServerSubscription } from "../useServerSubscription";
import { afTrack } from "../analytics/appsflyer";
import { Browser } from "@capacitor/browser";

export default function Settings({ userInfo, buyPlans, fetchBuyPlans }) {
  const [billingDate, setBillingDate] = useState(null);
  const [notifications, setNotifications] = useState({ buys: true });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const apiBase = import.meta.env.VITE_API_BASE_URL || "https://api.satstacker.app";
  const { token: ctxToken } = useContext(AuthContext);
  const jwt =
    getApiAccessToken() ||
    ctxToken ||
    "";

  const [strikeLinked, setStrikeLinked] = useState(null); // null = unknown, true/false = status
  const [expiresAt, setExpiresAt] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const { loading: subLoading, active: subActive, is_premium: subPremium, sub_expiry } = useServerSubscription();
  const prevStrikeLinked = useRef(null);

  useEffect(() => {
    // Fire only on transition to true
    if (prevStrikeLinked.current !== true && strikeLinked === true) {
      afTrack("strike_link_success", { entry: "settings" });
    }
    prevStrikeLinked.current = strikeLinked;
  }, [strikeLinked]);

  /* ------------------------------------------------------------------ */
  /* 1.  Compute next billing date anytime `buyPlans` or `userInfo` change
  /* ------------------------------------------------------------------ */
  useEffect(() => {

    if (!userInfo || !buyPlans?.length) return;

    const firstSmartPlan = buyPlans
      .filter(p => Number(p.user_id) === Number(userInfo.id))
      .find(p => p.smart_timing);

    if (!firstSmartPlan) { setBillingDate(null); return; }

    const nextRun   = new Date(firstSmartPlan.next_run);
    const anchorDay = nextRun.getUTCDate();
    const today     = new Date();
    const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);
    const daysInMonth = new Date(
      nextMonth.getFullYear(),
      nextMonth.getMonth() + 1,
      0
    ).getDate();

    const billing = new Date(
      nextMonth.getFullYear(),
      nextMonth.getMonth(),
      Math.min(anchorDay, daysInMonth)
    );

    setBillingDate(billing);
  }, [buyPlans, userInfo]);

  /* Ask parent to fetch plans once if we arrive with an empty list */
  useEffect(() => {
    if (!buyPlans?.length && fetchBuyPlans) fetchBuyPlans();
  }, []); // run on mount only

  useEffect(() => {
    const run = async () => {
      try {
        if (!jwt) { setStrikeLinked(false); return; }
        const res = await api.get(
          "/oauth/strike/status"
        );
        setStrikeLinked(Boolean(res.data?.linked));
        setExpiresAt(res.data?.expires_at || null);
        setAutoRefresh(Boolean(res.data?.auto_refresh));
      } catch {
        setStrikeLinked(false);
        setExpiresAt(null);
      }
    };
    run();
  }, [jwt]);

  useEffect(() => {
    const doIt = new URLSearchParams(window.location.search).get("do");
    if (doIt === "strike-connect") {
      // small timeout lets context hydrate before we read tokens
      setTimeout(() => handleConnectStrike(), 0);
    }
  }, []);

  /* ------------------------------------------------------------------ */
  /* 2.  Misc handlers (logout / delete)                                */
  /* ------------------------------------------------------------------ */
  async function clearNativePurchaseIdentity() {
    if (!isNative()) return;

    try {
      await Purchases.logOut();
    } catch (error) {
      console.warn("[RC] logOut failed", error);
    }

    window.__RC_LOGGED__ = undefined;
    window.__USER_ID__ = undefined;
  }

  async function handleLogout() {
    try {
      await clearNativePurchaseIdentity();
      await logoutSession();
    } finally {
      localStorage.removeItem(
        "has_logged_in_before"
      );

      localStorage.removeItem(
        "postLoginAction"
      );

      window.location.replace(
        "/login"
      );
    }
  }

  async function openManageSubscriptions() {
    if (!isNative()) {
      try {
        try {
          await refreshAccessToken();
        } catch {}

        const { data } = await api.get(
          "/api/subscriptions/revenuecat/management-url"
        );

        const url =
          data?.management_url;

        if (!url) {
          throw new Error(
            "No subscription management link was returned."
          );
        }

        window.location.assign(url);
      } catch (e) {
        console.error(
          "[Billing] could not open subscription manager",
          e
        );

        alert(
          "We couldn't open subscription management. Please try again."
        );
      }

      return;
    }

    const ua =
      (navigator?.userAgent || "")
        .toLowerCase();

    if (ua.includes("android")) {
      const sku =
        import.meta.env.VITE_GOOGLE_SUBS_SKU ||
        "smart_timing";

      const pkg =
        import.meta.env.VITE_ANDROID_PACKAGE ||
        "com.satstacker.app";

      const playUrl =
        `https://play.google.com/store/account/subscriptions?sku=${encodeURIComponent(
          sku
        )}&package=${encodeURIComponent(
          pkg
        )}`;

      window.open(
        playUrl,
        "_blank"
      );

      return;
    }

    try {
      window.location.href =
        "itms-apps://apps.apple.com/account/subscriptions";
    } catch {
      window.open(
        "https://apps.apple.com/account/subscriptions",
        "_blank"
      );
    }
  }

  async function handleDeleteAccount() {
    try {
      await api.delete("/users/me");

      await clearNativePurchaseIdentity();
      await logoutSession();

      localStorage.clear();
      sessionStorage.clear();

      window.location.replace(
        "/signup"
      );
    } catch (err) {
      console.error("Error deleting account:", err);
      alert("Failed to delete account. Please try again.");
    }
  }

  async function handleRestorePurchases() {
    try {
      if (isNative()) {
        await restorePurchases();        // triggers StoreKit / Play restore + backend sync
      } else {
        alert("Restore is only available on a mobile device.");
        return;
      }
      if (fetchBuyPlans) await fetchBuyPlans();  // refresh local UI from server state
      alert("Purchases restored. Your subscription status has been refreshed.");
    } catch (e) {
      console.error("Restore failed", e);
      alert(e?.message || "Could not restore purchases right now.");
    }
  }

  function getAccessToken(ctxToken) {
    return (
      getApiAccessToken() ||
      (typeof ctxToken === "string"
        ? ctxToken
        : "")
    );
  }
  
  async function handleConnectStrike() {
    try {
      afTrack("strike_link_started", { entry: "settings" });
      // 1) Silent refresh to update access token
      try {
        await refreshAccessToken();
      } catch {}

      // 2) Grab the freshest access token
      const access = getAccessToken(ctxToken);
        if (typeof access !== "string" || !access.length) {
          // remember intent and go to login
          localStorage.setItem("postLoginAction", "connectStrike");
          window.location.href = "/login";
          return;
        }

      // 3) Ask backend for a short-lived link token (valid ~5 min)
      const { data } = await api.post(
        "/oauth/strike/link-token"
      );

      const linkToken = data?.token;
      if (!linkToken) throw new Error("No link token");

      // 4) Kick off OAuth
      const url = `${apiBase}/oauth/strike/login?t=${encodeURIComponent(
        linkToken
      )}`;
      afTrack("strike_oauth_launched", { entry: "settings" });
      if (window?.Capacitor?.Browser?.open) {
        await window.Capacitor.Browser.open({ url });
      } else {
        window.location.href = url;
      }
    } catch (e) {
      console.error(e);
      alert("Could not start Strike connection. Please try again.");
      afTrack("strike_link_failed", {
        entry: "settings",
        error: e?.message || String(e),
      });
    }
  }

  async function handleRateSatStacker() {
    const ua =
      (navigator?.userAgent || "")
        .toLowerCase();

    const isAndroid =
      ua.includes("android");

    const url = isAndroid
      ? "https://play.google.com/store/apps/details?id=com.satstacker.app"
      : "https://apps.apple.com/app/id6748700654?action=write-review";

    afTrack("rate_satstacker_tapped", {
      entry: "settings",
      platform: isAndroid
        ? "android"
        : "ios",
    });

    try {
      if (isNative()) {
        await Browser.open({ url });
      } else {
        window.open(
          url,
          "_blank",
          "noopener,noreferrer"
        );
      }
    } catch (error) {
      console.warn(
        "[Settings] could not open review page",
        error
      );
    }
  }

  /* ------------------------------------------------------------------ */
  /* 3.  Render                                                         */
  /* ------------------------------------------------------------------ */
  return (
    <div className="space-y-6">
      {/* Card */}
      <div className="bg-card rounded-2xl p-8 shadow">
        <h1 className="text-3xl font-bold mb-6 text-center text-zinc-200">
          Settings
        </h1>

        {/* Profile */}
        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-4 text-zinc-200">Profile</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-zinc-200 mb-1">Email</label>
              <input
                type="email"
                value={userInfo?.email ?? "loading…"}
                readOnly
                disabled
                className="block w-full border border-zinc-700 rounded-md p-2 bg-zinc-800 text-zinc-300"
              />
            </div>
            <div>
              <Link to="/forgot-password" className="text-blue-600 hover:underline">
                Reset Password
              </Link>
            </div>
          </div>
        </section>

        {/* Strike Account */}
        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-4 text-zinc-200">
            Strike Account (required to buy)
          </h2>

          {strikeLinked === true && (
            <p className="text-green-700 mb-3">Connected to Strike ✅</p>
          )}
          {strikeLinked === false && (
            <p className="text-zinc-300 mb-3">Not connected yet.</p>
          )}

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleConnectStrike}
              className="inline-flex items-center px-4 py-2 rounded-md bg-black text-white hover:bg-gray-900"
            >
              {!strikeLinked
                ? "Connect Strike"
                : autoRefresh
                ? "Manage link"
                : "Re-link Strike"}
            </button>
          </div>
        </section>

        {/* Subscription */}
        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-3 text-zinc-200">
            Subscription
          </h2>

          <div className="rounded-xl border border-zinc-700 p-4">
            <div className="flex items-start justify-between">
              <div>
                <div className="text-zinc-200">Smart Timing Plan</div>
                {billingDate && (
                  <div className="mt-1 text-sm text-zinc-300">
                    Next billing:{" "}
                    <strong className="text-zinc-200">
                      {format(billingDate, "MMMM d, yyyy")}
                    </strong>
                  </div>
                )}
                {!subActive && (
                  <div className="mt-1 text-sm text-zinc-300">Not subscribed.</div>
                )}
              </div>
            </div>

            <div className="mt-4 space-y-2">
              {subActive ? (
                <>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={openManageSubscriptions}
                      className="inline-flex items-center justify-center rounded-md bg-blue-600 px-3 py-2 text-white hover:bg-blue-700"
                      title="Change your subscription"
                    >
                      Change Plan
                    </button>
                    <button
                      type="button"
                      onClick={openManageSubscriptions}
                      className="inline-flex items-center justify-center rounded-md border border-zinc-700 px-3 py-2 text-zinc-200 hover:bg-zinc-800"
                      title="Cancel your subscription"
                    >
                      Cancel Subscription
                    </button>
                  </div>

                  {isNative() && (
                    <button
                      type="button"
                      onClick={handleRestorePurchases}
                      className="text-sm text-zinc-200 underline hover:text-zinc-200"
                      title="Restore purchases on this device"
                    >
                      Restore Purchases
                    </button>
                  )}
                </>
              ) : (
                <>
                  <Link
                    to="/pricing"
                    className="inline-flex items-center justify-center rounded-md bg-blue-600 px-3 py-2 text-white hover:bg-blue-700"
                    title="Subscribe to Smart Timing"
                  >
                    View Pricing
                  </Link>

                  {isNative() && (
                    <button
                      type="button"
                      onClick={handleRestorePurchases}
                      className="block text-sm text-zinc-200 underline hover:text-zinc-200"
                      title="Restore purchases on this device"
                    >
                      Restore Purchases
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </section>

        {/* Notifications */}
        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-4 text-zinc-200">
            Notifications
          </h2>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={notifications.buys}
              onChange={() => setNotifications((n) => ({ ...n, buys: !n.buys }))}
              className="mr-2"
            />
            <span className="text-zinc-200">Buy confirmations</span>
          </label>
        </section>

        {/* Support */}
        <section>
          <h2 className="text-xl font-semibold mb-4 text-zinc-200">
            Support
          </h2>

          <div className="space-y-3">
            <a
              href="mailto:support@satstacker.app"
              className="block text-blue-500 hover:underline"
            >
              Email Support
            </a>

            {isNative() && (
              <button
                type="button"
                onClick={handleRateSatStacker}
                className="block text-left text-orange-400 hover:text-orange-300 hover:underline"
              >
                ⭐ Rate SatStacker
              </button>
            )}
          </div>
        </section>

        <hr className="my-8" />

        <button
          onClick={handleLogout}
          className="w-full bg-gray-300 hover:bg-gray-400 text-black font-semibold py-2 px-4 rounded-md transition"
        >
          Log out
        </button>

        <button
          onClick={() => setShowDeleteModal(true)}
          className="w-full mt-4 bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-4 rounded-md transition"
        >
          Delete Account
        </button>
      </div>

      {/* Outside the card */}
      <p className="text-sm text-red-400">
        <strong>Warning:</strong> Deleting your account will permanently erase all
        your data and cannot be undone.
      </p>

      <ConfirmModal
        isOpen={showDeleteModal}
        title="Delete Account?"
        message="This action is permanent and cannot be undone. Are you sure you want to delete your SatStacker account?"
        confirmText="Yes, delete it"
        cancelText="Cancel"
        onConfirm={handleDeleteAccount}
        onCancel={() => setShowDeleteModal(false)}
      />
    </div>
  );
}
